import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Github, Download, Zap, Mail, ShoppingCart, BarChart3, Users, Globe, Database, Clock, CheckCircle, ExternalLink, Workflow } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { trackPlausibleEvent } from '../utils/tracking';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { StatCard } from '../components/ui/StatCard';
import { StepCard } from '../components/ui/StepCard';
import { WhatNextSection } from '../components/WhatNextSection';

const N8N_CATEGORIES = [
    { name: 'AI & Machine Learning', count: 25, icon: <Zap className="w-5 h-5" /> },
    { name: 'Email Automation', count: 18, icon: <Mail className="w-5 h-5" /> },
    { name: 'E-commerce', count: 22, icon: <ShoppingCart className="w-5 h-5" /> },
    { name: 'Analytics & Reporting', count: 15, icon: <BarChart3 className="w-5 h-5" /> },
    { name: 'CRM & Sales', count: 20, icon: <Users className="w-5 h-5" /> },
    { name: 'Social Media', count: 30, icon: <Globe className="w-5 h-5" /> },
    { name: 'Data Processing', count: 25, icon: <Database className="w-5 h-5" /> },
    { name: 'Scheduling', count: 15, icon: <Clock className="w-5 h-5" /> },
];

const FEATURED_TEMPLATES = [
    {
        title: 'AI Email Responder',
        description: 'Automatycznie analizuje przychodzące e-maile i generuje inteligentne odpowiedzi z użyciem GPT-4.',
        category: 'AI & Email',
        downloads: '2.3k',
    },
    {
        title: 'Lead Scoring Automation',
        description: 'Ocenia i kwalifikuje leady z formularzy na podstawie kryteriów AI, przypisując je do odpowiednich handlowców.',
        category: 'CRM & Sales',
        downloads: '1.8k',
    },
    {
        title: 'Social Media Content Pipeline',
        description: 'Kompletny pipeline do tworzenia, planowania i publikowania treści na wielu platformach jednocześnie.',
        category: 'Social Media',
        downloads: '3.1k',
    },
    {
        title: 'E-commerce Order Sync',
        description: 'Synchronizuje zamówienia między Shopify, Allegro i WooCommerce z automatycznym powiadamianiem klientów.',
        category: 'E-commerce',
        downloads: '2.7k',
    },
    {
        title: 'Invoice Processing AI',
        description: 'Automatycznie wyciąga dane z faktur PDF i wprowadza je do systemu księgowego.',
        category: 'Finance',
        downloads: '1.5k',
    },
    {
        title: 'Customer Feedback Analyzer',
        description: 'Zbiera opinie z różnych kanałów, analizuje sentyment i tworzy raporty z rekomendacjami.',
        category: 'Analytics',
        downloads: '1.2k',
    },
];

const CategoryCard: React.FC<{ category: typeof N8N_CATEGORIES[0], index: number }> = ({ category, index }) => (
    <RevealOnScroll delay={index * 50}>
        <GlassCard variant="subtle" hover className="h-full">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                    {category.icon}
                </div>
                <div>
                    <h3 className="font-semibold text-sm text-text-primary-light dark:text-text-primary-dark">{category.name}</h3>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{category.count} szablonów</p>
                </div>
            </div>
        </GlassCard>
    </RevealOnScroll>
);

const TemplateCard: React.FC<{ template: typeof FEATURED_TEMPLATES[0], index: number }> = ({ template, index }) => (
    <RevealOnScroll delay={index * 100}>
        <GlassCard variant="medium" hover className="h-full">
            <div className="flex justify-between items-start mb-3">
                <span className="text-xs bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark px-3 py-1 rounded-full font-medium">
                    {template.category}
                </span>
                <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark flex items-center gap-1">
                    <Download className="w-3 h-3" /> {template.downloads}
                </span>
            </div>
            <h3 className="text-lg font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">{template.title}</h3>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">{template.description}</p>
        </GlassCard>
    </RevealOnScroll>
);

export const SzablonyN8nPage: React.FC = () => {
    usePageMetadata({
        title: "200+ Darmowych Szablonów n8n | Gotowe Workflow Automatyzacji",
        description: "Pobierz ponad 200 darmowych szablonów n8n do automatyzacji biznesu. AI, e-commerce, marketing, CRM i wiele więcej. Open-source na GitHub.",
        schema: {
            "@context": "https://schema.org",
            "@type": "SoftwareSourceCode",
            "name": "n8n Free Templates by BiznesBezKlikania",
            "description": "Kolekcja ponad 200 darmowych szablonów workflow n8n do automatyzacji procesów biznesowych",
            "codeRepository": "https://github.com/gacabartosz/n8n-free-templates",
            "programmingLanguage": "JSON",
            "author": {
                "@type": "Person",
                "name": "Bartosz Gaca",
                "url": "https://bartoszgaca.pl"
            },
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl",
                "url": "https://biznesbezklikania.pl"
            },
            "license": "https://opensource.org/licenses/MIT",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "PLN"
            }
        },
        ogData: {
            type: 'website'
        }
    });

    const stats = [
        { value: '200+', label: 'Szablonów' },
        { value: '20+', label: 'Kategorii' },
        { value: '100%', label: 'Darmowe' },
        { value: 'MIT', label: 'Licencja' }
    ];

    const steps = [
        { number: 1, title: 'Pobierz szablon z GitHub', description: 'Znajdź interesujący Cię szablon i pobierz plik JSON.' },
        { number: 2, title: 'Zaimportuj do n8n', description: 'W n8n kliknij "Import from file" i wybierz pobrany plik JSON.' },
        { number: 3, title: 'Skonfiguruj credentials', description: 'Dodaj swoje klucze API i dane dostępu do zintegrowanych usług.' },
        { number: 4, title: 'Gotowe!', description: 'Twój workflow jest gotowy do pracy. Aktywuj go i ciesz się automatyzacją!', isLast: true }
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <RevealOnScroll>
                            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                                <Workflow className="w-10 h-10 text-white" />
                            </div>
                            <div className="inline-flex items-center gap-2 bg-gray-900/10 dark:bg-white/10 text-text-primary-light dark:text-text-primary-dark px-4 py-2 rounded-full mb-6">
                                <Github className="w-5 h-5" />
                                <span className="font-semibold">Open Source</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                                200+ Darmowych Szablonów n8n
                            </h1>
                        </RevealOnScroll>

                        <RevealOnScroll delay={100}>
                            <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark mb-8 max-w-2xl mx-auto">
                                Gotowe workflow do automatyzacji Twojego biznesu. AI, e-commerce, marketing, CRM i wiele więcej.
                                Pobierz, zaimportuj do n8n i zacznij automatyzować!
                            </p>
                        </RevealOnScroll>

                        <RevealOnScroll delay={200}>
                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <a
                                    href="https://github.com/gacabartosz/n8n-free-templates"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackPlausibleEvent('github_click', { repo: 'n8n-templates' })}
                                    className="px-8 py-4 font-semibold text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-xl hover:opacity-90 transition-all text-lg inline-flex items-center justify-center gap-2"
                                >
                                    <Github className="w-5 h-5" />
                                    Zobacz na GitHub
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                                <LeadFormModal
                                    productSlug="szablony-n8n"
                                    triggerLabel="Potrzebujesz custom workflow?"
                                    triggerClassName="px-8 py-4 font-semibold text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 rounded-xl hover:bg-primary-light/20 dark:hover:bg-primary-dark/20 transition-colors text-lg inline-flex items-center justify-center gap-2"
                                />
                            </div>
                        </RevealOnScroll>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Stats Section */}
            <section className="py-12 relative">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {stats.map((stat, index) => (
                            <RevealOnScroll key={index} delay={index * 100}>
                                <StatCard value={stat.value} label={stat.label} variant={index === 0 ? 'highlight' : 'default'} />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                            Kategorie Szablonów
                        </h2>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
                            Przeglądaj szablony pogrupowane tematycznie. Każdy szablon jest gotowy do użycia.
                        </p>
                    </RevealOnScroll>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {N8N_CATEGORIES.map((category, index) => (
                            <CategoryCard key={index} category={category} index={index} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Templates */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                            Najpopularniejsze Szablony
                        </h2>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
                            Sprawdzone workflow, które pomagają firmom oszczędzać czas i pieniądze.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURED_TEMPLATES.map((template, index) => (
                            <TemplateCard key={index} template={template} index={index} />
                        ))}
                    </div>

                    <RevealOnScroll delay={300} className="text-center mt-12">
                        <a
                            href="https://github.com/gacabartosz/n8n-free-templates"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackPlausibleEvent('github_click', { repo: 'n8n-templates', placement: 'featured' })}
                            className="font-semibold text-primary-light dark:text-primary-dark inline-flex items-center gap-2 hover:underline"
                        >
                            Zobacz wszystkie szablony na GitHub
                            <ArrowRight className="w-5 h-5" />
                        </a>
                    </RevealOnScroll>
                </div>
            </section>

            {/* How to Use Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                            Jak używać szablonów?
                        </h2>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
                            Proces importu jest prosty i zajmuje tylko kilka minut.
                        </p>
                    </RevealOnScroll>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {steps.map((step, index) => (
                            <RevealOnScroll key={index} delay={index * 100}>
                                <StepCard
                                    number={step.number}
                                    title={step.title}
                                    description={step.description}
                                    variant="timeline"
                                    icon={step.isLast ? <CheckCircle className="w-5 h-5" /> : undefined}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 md:py-20 relative overflow-hidden">
                <AnimatedBlob variant="purple" />
                <ParticlesBackground variant="minimal" />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="max-w-3xl mx-auto text-center">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                                Potrzebujesz dedykowanego rozwiązania?
                            </h2>
                            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mb-8">
                                Szablony to dopiero początek. Jeśli potrzebujesz custom workflow dostosowanego do Twojego biznesu,
                                chętnie pomogę. Umów się na bezpłatną konsultację.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <LeadFormModal
                                    productSlug="szablony-n8n"
                                    triggerLabel="Umów bezpłatną konsultację"
                                    triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center justify-center gap-2 animate-glow"
                                />
                                <Link
                                    to="/produkty"
                                    className="px-8 py-4 font-semibold text-primary-light dark:text-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 rounded-xl hover:bg-primary-light/20 dark:hover:bg-primary-dark/20 transition-colors text-lg inline-flex items-center justify-center"
                                >
                                    Zobacz wszystkie produkty
                                </Link>
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            {/* Author Section */}
            <section className="py-16 md:py-20 relative">
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="medium" className="max-w-2xl mx-auto">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center text-white text-2xl font-bold">
                                    BG
                                </div>
                                <div>
                                    <h3 className="font-bold text-xl text-text-primary-light dark:text-text-primary-dark">Bartosz Gaca</h3>
                                    <p className="text-text-secondary-light dark:text-text-secondary-dark">Full-Stack Developer & AI Automation Expert</p>
                                </div>
                            </div>
                            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4">
                                Tworzę te szablony na podstawie realnych wdrożeń dla moich klientów. Każdy z nich był testowany w produkcji
                                i pomaga firmom oszczędzać setki godzin miesięcznie.
                            </p>
                            <div className="flex gap-4">
                                <a
                                    href="https://bartoszgaca.pl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-light dark:text-primary-dark font-semibold hover:underline inline-flex items-center gap-1"
                                >
                                    bartoszgaca.pl <ExternalLink className="w-4 h-4" />
                                </a>
                                <a
                                    href="https://github.com/gacabartosz"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-light dark:hover:text-primary-dark inline-flex items-center gap-1 transition-colors"
                                >
                                    <Github className="w-4 h-4" /> GitHub
                                </a>
                            </div>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            <WhatNextSection />
        </>
    );
};
